import { SpiceLevel } from "@badorbad/shared";
import { ChiliRow } from "./Chili";
import { palette } from "./tokens";

const LEVELS = [SpiceLevel.Mild, SpiceLevel.Medium, SpiceLevel.Hot] as const;
const LABEL: Record<SpiceLevel, string> = {
  [SpiceLevel.Mild]: "mild",
  [SpiceLevel.Medium]: "spicy",
  [SpiceLevel.Hot]: "hot",
};

/**
 * The spice control: a segmented row of three cells, each showing its level as
 * a bouquet of chilis.
 *
 * Three fixed targets beat a draggable track here. The scale has exactly three
 * values, so dragging asks the player to aim at a stop when they could simply
 * tap the one they want — and the whole control still fits one row of the
 * writing screen, which has two option halves to accommodate.
 *
 * The only implementation of this control; the exploration page renders the
 * same component, so the two cannot drift.
 */
export const SpiceSegmented = ({
  value,
  onChange,
  title = "How bad?",
  trackColor = palette.surface,
}: {
  value: SpiceLevel;
  onChange: (v: SpiceLevel) => void;
  title?: string;
  /**
   * The unselected track. `surface` over the page, as approved. A caller that
   * places this on `surface` itself must pass something else, or the control
   * disappears — see the settings sheet, which uses `bg` as its panel for
   * exactly this reason.
   */
  trackColor?: string;
}) => (
  <div>
    <div className="mb-1.5 text-[10px] uppercase tracking-widest text-ink-dim">
      {title}
    </div>
    <div
      className="flex overflow-hidden rounded-full"
      style={{
        backgroundColor: trackColor,
        // A hairline keeps the control's own edges legible even when the track
        // and its surroundings are close in value.
      }}
      role="radiogroup"
      aria-label="Spice level"
    >
      {LEVELS.map((lvl) => {
        const selected = value === lvl;
        return (
          <button
            key={lvl}
            role="radio"
            aria-checked={selected}
            aria-label={LABEL[lvl]}
            onClick={() => onChange(lvl)}
            className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2"
            style={{
              backgroundColor: selected ? palette.ink : "transparent",
              // Unselected cells keep full-colour chilis — dimming them would
              // make the scale harder to read than the selection is worth.
              opacity: selected ? 1 : 0.62,
            }}
          >
            {/* Slight overlap: the cluster reads as one mark, not a tally. */}
            <ChiliRow level={lvl} size={20} showEmpty={false} gap={-4} />
            <span
              className="text-[9px] uppercase tracking-widest"
              style={{ color: selected ? palette.bg : palette.inkDim }}
            >
              {LABEL[lvl]}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);
